
import { useTranslation } from 'react-i18next';

export default function Values() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const values = [
    {
      title: t('values.integrity'),
      description: t('values.integrityDesc'),
      icon: 'ri-shield-check-line',
    },
    {
      title: t('values.longTerm'),
      description: t('values.longTermDesc'),
      icon: 'ri-time-line',
    },
    {
      title: t('values.excellence'),
      description: t('values.excellenceDesc'),
      icon: 'ri-award-line',
    },
    {
      title: t('values.innovation'),
      description: t('values.innovationDesc'),
      icon: 'ri-lightbulb-line',
    },
    {
      title: t('values.collaboration'),
      description: t('values.collaborationDesc'),
      icon: 'ri-team-line',
    },
  ];

  return (
    <section id="values" className="py-32 bg-cream relative overflow-hidden" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 10% 20%, #D4AF37 1px, transparent 1px),
                           radial-gradient(circle at 90% 80%, #D4AF37 1px, transparent 1px)`,
          backgroundSize: '60px 60px',
        }}></div>
      </div>

      <div className="max-w-[1400px] mx-auto px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-px bg-gold"></div>
            <span className="text-gold text-sm font-medium tracking-widest uppercase">{t('values.label')}</span>
            <div className="w-12 h-px bg-gold"></div>
          </div>
          <h2 className="font-serif text-5xl lg:text-6xl text-navy mb-6">
            {t('values.title1')} <span className="text-gold">{t('values.title2')}</span>
          </h2>
          <p className="text-lg text-charcoal-light max-w-2xl mx-auto">
            {t('values.description')}
          </p>
        </div>

        {/* Values Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {values.map((value, index) => (
            <div 
              key={index} 
              className={`group ${index === 4 ? 'lg:col-start-2' : ''}`}
            >
              <div className="relative bg-white rounded-3xl p-8 h-full border border-cream-dark hover:border-gold/30 hover:shadow-2xl transition-all duration-500 overflow-hidden">
                {/* Background Glow */}
                <div className={`absolute top-0 ${isRTL ? 'left-0' : 'right-0'} w-32 h-32 bg-gold/5 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

                {/* Icon */}
                <div className="relative w-16 h-16 bg-navy rounded-2xl flex items-center justify-center mb-6 group-hover:bg-gold transition-colors duration-500">
                  <i className={`${value.icon} text-3xl text-white`}></i>
                </div>

                {/* Content */}
                <h3 className="text-xl font-semibold text-navy mb-4 group-hover:text-gold transition-colors duration-300">
                  {value.title}
                </h3>
                <p className="text-charcoal-light leading-relaxed text-sm">
                  {value.description}
                </p>

                {/* Decorative Line */}
                <div className={`mt-6 w-0 h-0.5 ${isRTL ? 'bg-gradient-to-l mr-auto' : 'bg-gradient-to-r'} from-gold to-gold-light group-hover:w-full transition-all duration-700`}></div>

                {/* Number */}
                <div className={`absolute top-6 ${isRTL ? 'left-6' : 'right-6'} text-6xl font-serif text-cream-dark group-hover:text-gold/10 transition-colors duration-300`}>
                  0{index + 1}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom Quote */}
        <div className="mt-24 relative">
          <div className="bg-gradient-to-br from-navy via-navy-light to-navy rounded-3xl p-12 lg:p-16 text-center overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gold rounded-full blur-3xl"></div>
            </div>

            <div className="relative max-w-4xl mx-auto">
              <i className={`ri-double-quotes-${isRTL ? 'r' : 'l'} text-6xl text-gold/30 mb-6 block`}></i>
              <p className="text-2xl lg:text-3xl font-serif text-white leading-relaxed mb-8">
                {t('values.quote')}
              </p>
              <div className="flex items-center justify-center gap-4">
                <div className="w-16 h-px bg-gold"></div>
                <span className="text-gold font-medium">{isRTL ? 'الأبل للاستثمار' : 'ALEBEL Investment'}</span>
                <div className="w-16 h-px bg-gold"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
